import React from 'react'
import { useParams } from 'react-router'
import { CompletionStatus, CreditApplicationPerson, SectionName } from '_gql'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import Banner, { BannerColor } from '@divvy-web/skylab.banner'
import Icon, { ICON_SIZE_REGULAR } from '@divvy-web/skylab.icon'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'
import { TOAST_TYPE_DANGER, TOAST_TYPE_SUCCESS, useToast } from '@divvy-web/skylab.toast'

import { useApplicationProgress } from '@sputnik/pages/CreditApplicationPages/Navigation/useApplicationProgress'
import { useUpdatePeopleInfoData } from '@sputnik/pages/CreditApplicationPages/PeopleInfoForm/UpdatePeopleInfoData.gql'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import styles from './styles.module.scss'

interface AddAuthorizedSignerBannerProps {
  authorizedSigner: Pick<CreditApplicationPerson, 'firstName' | 'lastName' | 'email' | 'id'>
  onClick: () => void
}

const AddAuthorizedSignerBanner = ({ authorizedSigner, onClick }: AddAuthorizedSignerBannerProps) => {
  const { appId } = useParams()
  const showToast = useToast()
  const [getClassName] = useNameFormatter('AddAuthorizedSignerBanner')
  const completionStatus = useApplicationProgress({ sectionName: SectionName.PEOPLE_INFO })
  const { track } = useWebTracking()

  const { firstName, lastName } = authorizedSigner || {}

  const authorizedSignerName = (
    <span>
      {firstName} {lastName}
    </span>
  )

  const [updateCompanyOwner] = useUpdatePeopleInfoData({
    onCompleted: (updatedData) => {
      const peopleInfoSectionComplete = updatedData.updateCreditApplication?.savedSectionsInfo?.find(
        (section) =>
          section?.sectionName === SectionName.BUSINESS_INFO &&
          section?.completionStatus === CompletionStatus.COMPLETED,
      )

      if (peopleInfoSectionComplete && completionStatus !== CompletionStatus.COMPLETED) {
        track('se_signup__application_people_info_section_progress__completed', {
          is_complete: true,
        })
      }

      logInfo('onAddAuthSignerAsCompanyOwner', {
        attributes: {
          creditAppId: appId,
          result: `added auth signer as company owner successfully`,
        },
        eventName: 'onAddAuthSignerAsCompanyOwner',
      })
      showToast(
        TOAST_TYPE_SUCCESS,
        <FormattedMessage
          defaultMessage='Authorized signer added as company owner'
          id='sputnik.AddAuthorizedSignerBanner__Lj6s4h'
        />,
        {
          autoHideDelay: 5000,
        },
      )
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Failed to add authorized signer as company owner'
          id='sputnik.AddAuthorizedSignerBanner__ANEsjc'
        />,
        {
          className: 'dd-privacy-allow',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'onAddAuthSignerAsCompanyOwner',
          creditAppId: appId,
          result: error,
          message: 'Error while adding authorized signer as company owner.',
        },
        eventName: 'onAddAuthSignerAsCompanyOwner',
      })
    },
  })

  const onAddAuthSignerAsCompanyOwner = () => {
    track('se_signup__add_auth_signer_as_company_owner__fired')

    updateCompanyOwner({
      variables: {
        beneficialOwners: [
          {
            id: authorizedSigner?.id,
            email: authorizedSigner?.email,
          },
        ],
        creditApplicationId: appId ?? '',
      },
    })
  }

  return (
    <div className={styles[getClassName('')]}>
      <Banner
        backgroundColor={BannerColor.Info}
        customIcon={
          <Icon
            name='payroll'
            size={ICON_SIZE_REGULAR}
          />
        }
        messageContent={
          <FormattedMessage
            defaultMessage='To quickly add the authorized signer as an owner <link>click here</link>.'
            id='sputnik.AddAuthorizedSignerBanner__VEWLeu'
            values={{
              link: (chunks) => (
                <TextLink
                  size={TextLinkSizes.REGULAR}
                  onClick={onAddAuthSignerAsCompanyOwner}
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        }
        titleText={
          <FormattedMessage
            defaultMessage='Is {authorizedSignerName} also an owner?'
            id='sputnik.AddAuthorizedSignerBanner__gepxrO'
            values={{ authorizedSignerName }}
          />
        }
        onClose={onClick}
      />
    </div>
  )
}

export default AddAuthorizedSignerBanner
